// src/pages/UsuariosPage.tsx
import { useState, useEffect } from 'react'
import { api } from '../services/api'
import { Users, Plus, Edit, Trash2, Unlock, Search, X } from 'lucide-react'

interface Usuario {
  id: number
  email: string
  nombre: string
  rol: string
  activo: number
  intentos_fallidos: number
  bloqueado_hasta: string | null
  created_at: string
}

export default function UsuariosPage() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filtroRol, setFiltroRol] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editando, setEditando] = useState<Usuario | null>(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    rol: 'DISTRIBUIDOR',
    activo: true
  })

  useEffect(() => {
    cargarUsuarios()
  }, [search, filtroRol])

  const cargarUsuarios = async () => {
    try {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (filtroRol) params.append('rol', filtroRol)
      
      const { data } = await api.get(`/usuarios?${params}`)
      setUsuarios(data.data)
    } catch (error) {
      console.error('Error cargando usuarios:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editando) {
        await api.put(`/usuarios/${editando.id}`, formData)
      } else {
        await api.post('/usuarios', formData)
      }
      setShowModal(false)
      setEditando(null)
      setFormData({ email: '', password: '', nombre: '', rol: 'DISTRIBUIDOR', activo: true })
      cargarUsuarios()
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error al guardar usuario')
    }
  }

  const handleEdit = (usuario: Usuario) => {
    setEditando(usuario)
    setFormData({
      email: usuario.email,
      password: '',
      nombre: usuario.nombre,
      rol: usuario.rol,
      activo: usuario.activo === 1
    })
    setShowModal(true)
  }

  const handleDesbloquear = async (id: number) => {
    if (!confirm('¿Desbloquear este usuario?')) return
    try {
      await api.put(`/usuarios/${id}/desbloquear`)
      cargarUsuarios()
    } catch (error) {
      alert('Error al desbloquear usuario')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Desactivar este usuario?')) return
    try {
      await api.delete(`/usuarios/${id}`)
      cargarUsuarios()
    } catch (error) {
      alert('Error al desactivar usuario')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="w-6 h-6" />
          Gestión de Usuarios
        </h1>
        <button
          onClick={() => {
            setEditando(null)
            setFormData({ email: '', password: '', nombre: '', rol: 'DISTRIBUIDOR', activo: true })
            setShowModal(true)
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nuevo Usuario
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow flex gap-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        <select
          value={filtroRol}
          onChange={(e) => setFiltroRol(e.target.value)}
          className="border rounded-lg px-4 py-2"
        >
          <option value="">Todos los roles</option>
          <option value="ADMINISTRADOR">Administrador</option>
          <option value="DISTRIBUIDOR">Distribuidor</option>
          <option value="CLIENTE">Cliente</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Nombre</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Email</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Rol</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  Cargando...
                </td>
              </tr>
            ) : usuarios.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No hay usuarios
                </td>
              </tr>
            ) : (
              usuarios.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{usuario.nombre}</td>
                  <td className="px-4 py-3 text-gray-600">{usuario.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      usuario.rol === 'ADMINISTRADOR' ? 'bg-purple-100 text-purple-700' :
                      usuario.rol === 'DISTRIBUIDOR' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {usuario.rol}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {usuario.bloqueado_hasta ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Bloqueado
                      </span>
                    ) : usuario.activo ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Activo
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Inactivo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {usuario.bloqueado_hasta && (
                        <button
                          onClick={() => handleDesbloquear(usuario.id)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                          title="Desbloquear"
                        >
                          <Unlock className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(usuario)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(usuario.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Desactivar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editando ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña {editando && '(dejar vacío para no cambiar)'}
                </label>
                <input
                  type="password"
                  required={!editando}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                <select
                  value={formData.rol}
                  onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ADMINISTRADOR">Administrador</option>
                  <option value="DISTRIBUIDOR">Distribuidor</option>
                  <option value="CLIENTE">Cliente</option>
                </select>
              </div>
              {editando && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="activo"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="activo" className="text-sm text-gray-700">Usuario activo</label>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editando ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
